import { useAbility } from '@casl/react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import EntityIcon from 'src/components/Icons/EntityIcon';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import type { MeterOnlineCounts } from 'switch-shared/modules/crud/meter/meter.dto';
import {
  AGNOSTIC_TAG,
  ELECTRICITY_TAG,
  FORECAST_TAG,
} from 'switch-shared/modules/crud/meter/meter.enums';
import { UtilityType } from 'switch-shared/modules/crud/utility/utility.enums';

import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import { useDashboard } from '../../../../context/dashboardContext';
import { useGetMetersCountByTagQuery } from '../../../../services/switch-rest-api/meter.service';
import { isAllSelection } from '../../../../utils/selection';
import MeterTagCounts from '../MeterTagCounts';

/** Tags counted; forecast meters are counted so that they stay out of "Other", and are not shown. */
const TAGS = [ELECTRICITY_TAG.SOLAR, AGNOSTIC_TAG.MUNICIPAL, FORECAST_TAG];

const toCount = (label: string, counts?: MeterOnlineCounts) => ({
  label,
  online: counts?.totalOnline ?? 0,
  total: (counts?.totalOnline ?? 0) + (counts?.totalOffline ?? 0),
});

/** Electricity meters on solar meter points, on municipal meter points and on neither, each with its online share. */
const MetersByTagWidget = () => {
  const theme = useTheme();
  const ability = useAbility(AbilityContext);
  const { zone } = useDashboard();

  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();
  const isAllGroupsSelected = isAllSelection(appGroupId);
  const isAllAppsSelected = isAllSelection(appId);
  const canReadMeters = ability.can(ACTIONS.READ, 'Meters');

  const { data, isLoading } = useGetMetersCountByTagQuery(
    {
      ...(!isAllAppsSelected && { applicationId: appId }),
      ...(!isAllGroupsSelected && { applicationGroupId: appGroupId }),
      utilityType: UtilityType.ELECTRICITY,
      zoneId: zone?._id,
      tags: TAGS.join(','),
    },
    {
      skip: !canReadMeters || appGroupId == null || isAllGroupsSelected,
      pollingInterval: 30_000,
    }
  );

  const byTag = (tag: string) => data?.tags.find((count) => count.tag === tag);

  return (
    <WidgetCard
      title='Electricity Meters'
      subheader={
        zone?.name
          ? `By meter point tag in ${zone.name}`
          : 'By meter point tag, with online status'
      }
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <EntityIcon type='meters' />
        </Box>
      }
      disabled={!canReadMeters}
      cardContentProps={{ sx: { pt: '0 !important', pb: '12px !important' } }}
    >
      {isLoading ? (
        <Box
          height='100%'
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          <CircularProgress />
        </Box>
      ) : (
        <MeterTagCounts
          counts={[
            toCount('Solar', byTag(ELECTRICITY_TAG.SOLAR)),
            toCount('Municipal', byTag(AGNOSTIC_TAG.MUNICIPAL)),
            toCount('Other', data?.other),
          ]}
        />
      )}
    </WidgetCard>
  );
};

export default MetersByTagWidget;
